import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell, BellOff, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, ExternalLink, Loader2, Plus, RefreshCw, Settings, Tag, X } from 'lucide-react'
import { useLocation } from 'react-router-dom'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
  StatusBadge,
  Textarea,
  useToast,
} from '@/components/ui'
import { TrackingTimeline, trackIssue, pauseActiveTracking } from './TrackingTimeline.tsx'
import { MonitorEntriesPanel } from './MonitorEntriesPanel.tsx'

// ─── Types ───────────────────────────────────────────────────────────────────

interface MonitoredIssue {
  id: string
  identifier: string
  title: string
  status: string
  priority: string
  updatedAt?: string
  parentId?: string | null
  isSubtask: boolean
  parentIdentifier?: string
  parentTitle?: string
  matchedLabels?: Array<{ id: string; name: string; color?: string }>
}

interface PaperclipLabel {
  id: string
  name: string
  color?: string
}

interface PaperclipAgent {
  id: string
  name: string
  role?: string
}

interface NotificationAttempt {
  title: string
  body: string
  tag: string
  type: 'status_change' | 'update' | 'run_finished'
  attemptedAt: string
  sentToOS: boolean
}

interface AgentRun {
  id: string
  agentId: string
  agentName: string
  status: 'running' | 'succeeded' | 'failed' | 'cancelled'
  startedAt: string
  finishedAt: string | null
}

// ─── Constants ────────────────────────────────────────────────────────────────

const STORAGE_SIDEBAR_OPEN = 'paperclip-monitor-sidebar-open'
const STORAGE_USER_ID = 'paperclip-monitor-userId'
const STORAGE_SEEN_STATUSES = 'paperclip-monitor-seenStatuses'
const STORAGE_SEEN_UPDATED_AT = 'paperclip-monitor-seenUpdatedAt'
const STORAGE_SEEN_RUN_STATUSES = 'paperclip-monitor-seenRunStatuses'
const STORAGE_LABEL_IDS = 'paperclip-monitor-labelIds'
const STORAGE_PROJECT_ID = 'paperclip-monitor-projectId'
const STORAGE_DEFAULT_AGENT_ID = 'paperclip-monitor-defaultAgentId'
const DEFAULT_USER_ID = 'S7H9xMKHveJjNviz0geAQw8V9xgJLqF6'
// Statuses that trigger a notification on first appearance / status change
const ALERT_STATUSES = new Set(['in_review', 'blocked', 'done'])
// Statuses where an update (new comment, etc.) without status change also notifies
const UPDATE_NOTIFY_STATUSES = new Set(['blocked'])
// Statuses shown in the active list
const ACTIVE_DISPLAY_STATUSES = new Set(['in_review', 'blocked', 'in_progress'])
const PAPERCLIP_BASE_URL = 'https://paperclip-ahr3.srv1655616.hstgr.cloud'
const CLOCK_FAVICON = `data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="%236366f1" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>`

function loadSeenStatuses(): Map<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_SEEN_STATUSES)
    if (!raw) return new Map()
    return new Map(Object.entries(JSON.parse(raw)))
  } catch {
    return new Map()
  }
}

function saveSeenStatuses(map: Map<string, string>) {
  localStorage.setItem(STORAGE_SEEN_STATUSES, JSON.stringify(Object.fromEntries(map)))
}

function loadSeenUpdatedAt(): Map<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_SEEN_UPDATED_AT)
    if (!raw) return new Map()
    return new Map(Object.entries(JSON.parse(raw)))
  } catch {
    return new Map()
  }
}

function saveSeenUpdatedAt(map: Map<string, string>) {
  localStorage.setItem(STORAGE_SEEN_UPDATED_AT, JSON.stringify(Object.fromEntries(map)))
}

function loadSeenRunStatuses(): Map<string, string> {
  try {
    const raw = localStorage.getItem(STORAGE_SEEN_RUN_STATUSES)
    if (!raw) return new Map()
    return new Map(Object.entries(JSON.parse(raw)))
  } catch {
    return new Map()
  }
}

function saveSeenRunStatuses(map: Map<string, string>) {
  localStorage.setItem(STORAGE_SEEN_RUN_STATUSES, JSON.stringify(Object.fromEntries(map)))
}

function formatRunDuration(startedAt: string, finishedAt: string | null): string {
  const end = finishedAt ? new Date(finishedAt).getTime() : Date.now()
  const secs = Math.floor((end - new Date(startedAt).getTime()) / 1000)
  if (secs < 60) return `${secs}s`
  const mins = Math.floor(secs / 60)
  const rest = secs % 60
  return rest > 0 ? `${mins}m ${rest}s` : `${mins}m`
}

function formatFinishedAgo(finishedAt: string): string {
  const secs = Math.floor((Date.now() - new Date(finishedAt).getTime()) / 1000)
  if (secs < 60) return 'gerade eben'
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `vor ${mins} Min.`
  const hrs = Math.floor(mins / 60)
  return `vor ${hrs} Std.`
}

function loadTrackedLabelIds(): string[] {
  try {
    const raw = localStorage.getItem(STORAGE_LABEL_IDS)
    if (!raw) return []
    return JSON.parse(raw) as string[]
  } catch {
    return []
  }
}

function saveTrackedLabelIds(ids: string[]) {
  localStorage.setItem(STORAGE_LABEL_IDS, JSON.stringify(ids))
}

function getIssueUrl(identifier: string): string {
  const prefix = identifier.split('-')[0]
  return `${PAPERCLIP_BASE_URL}/${prefix}/issues/${identifier}`
}

const STATUS_LABELS: Record<string, string> = {
  in_review: 'In Review',
  blocked: 'Blockiert',
  done: 'Erledigt',
  in_progress: 'In Bearbeitung',
}

// ─── API ──────────────────────────────────────────────────────────────────────

async function fetchMonitoredIssues(
  paperclipUserId: string,
  labelIds: string[],
): Promise<MonitoredIssue[]> {
  const params: Record<string, string> = { paperclipUserId }
  if (labelIds.length > 0) params['labelIds'] = labelIds.join(',')
  const { data } = await apiClient.get<MonitoredIssue[]>('/paperclip/monitor-issues', { params })
  return data
}

async function fetchLabels(): Promise<PaperclipLabel[]> {
  const { data } = await apiClient.get<PaperclipLabel[]>('/paperclip/labels')
  return Array.isArray(data) ? data : []
}

async function fetchAgents(): Promise<PaperclipAgent[]> {
  try {
    const { data } = await apiClient.get<PaperclipAgent[]>('/paperclip/agents')
    return Array.isArray(data) ? data : []
  } catch {
    return []
  }
}

async function fetchAgentRuns(limit = 30): Promise<AgentRun[]> {
  const { data } = await apiClient.get<AgentRun[]>('/paperclip/agent-runs', { params: { limit } })
  return Array.isArray(data) ? data : []
}

async function postCreateIssue(payload: {
  title: string
  description: string
  projectId: string
  assigneeAgentId: string
  labelIds?: string[]
}): Promise<{ id: string; identifier: string }> {
  const { data } = await apiClient.post<{ id: string; identifier: string }>(
    '/paperclip/issues',
    payload,
  )
  return data
}

// ─── LabelChip ────────────────────────────────────────────────────────────────

function LabelChip({
  label,
  onRemove,
}: {
  label: PaperclipLabel
  onRemove?: () => void
}) {
  const style = label.color
    ? { backgroundColor: `${label.color}22`, color: label.color, borderColor: `${label.color}44` }
    : undefined
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium"
      style={style}
    >
      <Tag className="h-3 w-3 shrink-0" />
      {label.name}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          className="ml-0.5 rounded-full hover:opacity-70"
          aria-label={`Label ${label.name} entfernen`}
        >
          <X className="h-3 w-3" />
        </button>
      )}
    </span>
  )
}

// ─── BroadcastChannel helpers ────────────────────────────────────────────────

function sendBrowserNotification(
  title: string,
  body: string,
  tag: string,
  requireInteraction: boolean,
  externalSentTags: Set<string>,
  bc: BroadcastChannel | null,
): void {
  if (!('Notification' in window) || Notification.permission !== 'granted') return
  if (externalSentTags.has(tag)) return
  // eslint-disable-next-line no-new
  new Notification(title, { body, icon: '/favicon.ico', tag, requireInteraction })
  bc?.postMessage({ type: 'sent', tag })
}

// ─── CreateIssueModal ─────────────────────────────────────────────────────────

function CreateIssueModal({
  open,
  onClose,
  defaultAgentId,
  projectId,
  agents,
  preselectedLabelIds,
  availableLabels,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  defaultAgentId: string
  projectId: string
  agents: PaperclipAgent[]
  preselectedLabelIds: string[]
  availableLabels: PaperclipLabel[]
  onCreated?: (issue: { id: string; identifier: string; title: string }) => void
}) {
  const { toast } = useToast()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [agentId, setAgentId] = useState(defaultAgentId)

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setTitle('')
      setDescription('')
      setAgentId(defaultAgentId)
    }
  }, [open, defaultAgentId])

  const mutation = useMutation({
    mutationFn: postCreateIssue,
    onSuccess: (issue, vars) => {
      toast({
        title: 'Issue erstellt',
        description: `${issue.identifier} wurde erfolgreich angelegt.`,
        tone: 'success',
      })
      onCreated?.({ id: issue.id, identifier: issue.identifier, title: vars.title })
      onClose()
    },
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (err instanceof Error ? err.message : 'Unbekannter Fehler')
      toast({ title: 'Fehler beim Erstellen', description: msg, tone: 'danger' })
    },
  })

  const canSubmit =
    title.trim().length > 0 &&
    description.trim().length > 0 &&
    agentId.trim().length > 0 &&
    !mutation.isPending

  function handleSubmit() {
    if (!canSubmit) return
    mutation.mutate({
      title: title.trim(),
      description: description.trim(),
      projectId,
      assigneeAgentId: agentId.trim(),
      labelIds: preselectedLabelIds.length > 0 ? preselectedLabelIds : undefined,
    })
  }

  const agentOptions = agents.length > 0 ? agents : defaultAgentId ? [{ id: defaultAgentId, name: defaultAgentId }] : []

  const labelObjects = preselectedLabelIds
    .map(id => availableLabels.find(l => l.id === id) ?? { id, name: id })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Neues Issue erstellen"
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={mutation.isPending}>
            Abbrechen
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={!canSubmit}
          >
            {mutation.isPending ? 'Wird erstellt…' : 'Issue erstellen'}
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">
            Titel <span className="text-red-500">*</span>
          </label>
          <Input
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="Kurze Beschreibung des Issues…"
            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSubmit()}
            autoFocus
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">
            Beschreibung <span className="text-red-500">*</span>
          </label>
          <Textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Ausführliche Beschreibung, Schritte zur Reproduktion, Kontext…"
            rows={5}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="text-sm font-medium">Agent</label>
          {agentOptions.length > 1 ? (
            <SelectRoot value={agentId} onValueChange={setAgentId}>
              <SelectTrigger>
                <SelectValue placeholder="Agent auswählen…" />
              </SelectTrigger>
              <SelectContent>
                {agentOptions.map(agent => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </SelectRoot>
          ) : agentOptions.length === 1 ? (
            <Input value={agentOptions[0]!.name} readOnly className="bg-gray-50 text-gray-600" />
          ) : (
            <p className="text-sm text-muted-foreground italic">
              Kein Agent konfiguriert — bitte in den Einstellungen einen Standard-Agenten hinterlegen.
            </p>
          )}
        </div>

        {labelObjects.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <label className="text-sm font-medium">Labels (aus aktivem Filter)</label>
            <div className="flex flex-wrap gap-1.5">
              {labelObjects.map(label => (
                <LabelChip key={label.id} label={label} />
              ))}
            </div>
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Das Issue wird dem konfigurierten Paperclip-Projekt zugeordnet und mit Status{' '}
          <strong>Todo</strong> angelegt.
        </p>
      </div>
    </Modal>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export function PaperclipMonitorPage() {
  const queryClient = useQueryClient()
  const location = useLocation()

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(
    () => localStorage.getItem(STORAGE_SIDEBAR_OPEN) !== 'false',
  )

  function toggleSidebar() {
    setSidebarOpen(prev => {
      const next = !prev
      localStorage.setItem(STORAGE_SIDEBAR_OPEN, next ? 'true' : 'false')
      return next
    })
  }

  const [userId, setUserId] = useState<string>(
    () => localStorage.getItem(STORAGE_USER_ID) ?? DEFAULT_USER_ID,
  )
  const [userIdInput, setUserIdInput] = useState<string>(userId)
  const [projectId, setProjectId] = useState<string>(
    () => localStorage.getItem(STORAGE_PROJECT_ID) ?? '',
  )
  const [projectIdInput, setProjectIdInput] = useState<string>(projectId)
  const [defaultAgentId, setDefaultAgentId] = useState<string>(
    () => localStorage.getItem(STORAGE_DEFAULT_AGENT_ID) ?? '',
  )
  const [showSettings, setShowSettings] = useState(false)
  const [showCreateIssue, setShowCreateIssue] = useState(false)
  const [notifPermission, setNotifPermission] = useState<NotificationPermission>(
    () => ('Notification' in window ? Notification.permission : 'denied'),
  )
  const [trackedLabelIds, setTrackedLabelIds] = useState<string[]>(loadTrackedLabelIds)
  const [labelSearchInput, setLabelSearchInput] = useState('')
  const [showDone, setShowDone] = useState(false)
  const [lastNotif, setLastNotif] = useState<NotificationAttempt | null>(null)
  const pollCountRef = useRef(0)
  const tabBlinkRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const seenStatusesRef = useRef<Map<string, string>>(loadSeenStatuses())
  const seenUpdatedAtRef = useRef<Map<string, string>>(loadSeenUpdatedAt())
  const seenRunStatusesRef = useRef<Map<string, string>>(loadSeenRunStatuses())
  const bcRef = useRef<BroadcastChannel | null>(null)
  const externalSentTagsRef = useRef<Set<string>>(new Set())

  // Set clock favicon and title for this page; restore on unmount.
  useEffect(() => {
    const originalTitle = document.title
    document.title = '⏰ Paperclip Monitor'

    let faviconLink = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
    const prevFaviconHref = faviconLink?.href ?? null
    if (!faviconLink) {
      faviconLink = document.createElement('link')
      faviconLink.rel = 'icon'
      document.head.appendChild(faviconLink)
    }
    faviconLink.href = CLOCK_FAVICON

    return () => {
      document.title = originalTitle
      const icon = document.querySelector<HTMLLinkElement>('link[rel="icon"]')
      if (icon) {
        if (prevFaviconHref) {
          icon.href = prevFaviconHref
        } else {
          icon.remove()
        }
      }
    }
  }, [])

  // Pause tracking when page is hidden or tab loses focus; resume on return.
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        void pauseActiveTracking().catch(() => undefined)
      }
    }
    const onPageHide = () => {
      void pauseActiveTracking().catch(() => undefined)
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [])

  // Pause tracking when navigating away from the monitor page.
  const isMonitorPage = location.pathname.includes('paperclip')
  const prevIsMonitorRef = useRef(isMonitorPage)
  useEffect(() => {
    if (prevIsMonitorRef.current && !isMonitorPage) {
      void pauseActiveTracking().catch(() => undefined)
    }
    prevIsMonitorRef.current = isMonitorPage
  }, [isMonitorPage])

  const handleIssueClick = useCallback((issue: MonitoredIssue) => {
    void trackIssue(issue.identifier, issue.title)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['tracking-entries'] })
        void queryClient.invalidateQueries({ queryKey: ['tracking-active'] })
      })
      .catch(() => undefined)
  }, [queryClient])

  const handleNewIssueCreated = useCallback((issue: { id: string; identifier: string; title: string }) => {
    void trackIssue(issue.identifier, issue.title)
      .then(() => {
        void queryClient.invalidateQueries({ queryKey: ['tracking-entries'] })
        void queryClient.invalidateQueries({ queryKey: ['tracking-active'] })
      })
      .catch(() => undefined)
  }, [queryClient])

  useEffect(() => {
    if (!('BroadcastChannel' in window)) return
    const bc = new BroadcastChannel('paperclip-notifications')
    bcRef.current = bc
    bc.onmessage = (ev: MessageEvent<{ type: string; tag: string }>) => {
      if (ev.data?.type === 'sent') {
        externalSentTagsRef.current.add(ev.data.tag)
        setTimeout(() => externalSentTagsRef.current.delete(ev.data.tag), 10_000)
      }
    }
    return () => {
      bc.close()
      bcRef.current = null
    }
  }, [])

  const { data: availableLabels = [] } = useQuery({
    queryKey: ['paperclip-labels'],
    queryFn: fetchLabels,
    staleTime: 5 * 60_000,
  })

  const { data: availableAgents = [] } = useQuery({
    queryKey: ['paperclip-agents'],
    queryFn: fetchAgents,
    staleTime: 5 * 60_000,
  })

  const { data: issues, isLoading, isError, error, dataUpdatedAt, refetch } = useQuery({
    queryKey: ['paperclip-monitor-issues', userId, trackedLabelIds],
    queryFn: () => fetchMonitoredIssues(userId, trackedLabelIds),
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
    enabled: !!userId,
  })

  const { data: agentRuns = [] } = useQuery({
    queryKey: ['paperclip-agent-runs'],
    queryFn: () => fetchAgentRuns(30),
    refetchInterval: 15_000,
    refetchIntervalInBackground: true,
  })

  const stopTabBlink = useCallback(() => {
    if (tabBlinkRef.current) {
      clearInterval(tabBlinkRef.current)
      tabBlinkRef.current = null
    }
    document.title = '⏰ Paperclip Monitor'
  }, [])

  const startTabBlink = useCallback(() => {
    if (tabBlinkRef.current) return
    let show = true
    tabBlinkRef.current = setInterval(() => {
      document.title = show ? '🔴 Neue Meldung' : '⏰ Paperclip Monitor'
      show = !show
    }, 1000)
  }, [])

  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        stopTabBlink()
        void refetch()
      }
    }
    document.addEventListener('visibilitychange', handleVisibility)
    return () => document.removeEventListener('visibilitychange', handleVisibility)
  }, [refetch, stopTabBlink])

  useEffect(() => () => stopTabBlink(), [stopTabBlink])

  useEffect(() => {
    if (!issues) return

    pollCountRef.current += 1
    const seen = seenStatusesRef.current
    const seenUpdatedAt = seenUpdatedAtRef.current

    const attemptNotif = (
      title: string,
      body: string,
      tag: string,
      requireInteraction: boolean,
      type: 'status_change' | 'update',
    ) => {
      const sentToOS = notifPermission === 'granted' && !externalSentTagsRef.current.has(tag)
      setLastNotif({
        title,
        body,
        tag,
        type,
        attemptedAt: new Date().toISOString(),
        sentToOS,
      })
      if (document.visibilityState !== 'visible') {
        startTabBlink()
      }
      if (notifPermission === 'granted') {
        sendBrowserNotification(title, body, tag, requireInteraction, externalSentTagsRef.current, bcRef.current)
      }
    }

    for (const issue of issues) {
      const prevStatus = seen.get(issue.id)
      const prevUpdatedAt = seenUpdatedAt.get(issue.id)
      const statusChanged = prevStatus !== issue.status
      const updatedAtChanged =
        prevUpdatedAt !== undefined &&
        issue.updatedAt !== undefined &&
        prevUpdatedAt !== issue.updatedAt
      const isBlocked = issue.status === 'blocked'
      const isDone = issue.status === 'done'

      const body = issue.isSubtask && issue.parentIdentifier
        ? `Subtask von ${issue.parentIdentifier}: ${issue.title}`
        : issue.title

      if (ALERT_STATUSES.has(issue.status) && statusChanged) {
        const label = STATUS_LABELS[issue.status] ?? issue.status
        attemptNotif(
          `${isBlocked ? '🚨 ' : isDone ? '✅ ' : ''}${issue.identifier}: ${label}`,
          body,
          `paperclip-${issue.id}-${issue.status}`,
          isBlocked || isDone,
          'status_change',
        )
      }

      if (UPDATE_NOTIFY_STATUSES.has(issue.status) && !statusChanged && updatedAtChanged) {
        const label = STATUS_LABELS[issue.status] ?? issue.status
        attemptNotif(
          `📝 ${issue.identifier}: Update (${label})`,
          body,
          `paperclip-${issue.id}-update-${issue.updatedAt}`,
          isBlocked,
          'update',
        )
      }
    }

    for (const issue of issues) {
      seen.set(issue.id, issue.status)
      if (issue.updatedAt) {
        seenUpdatedAt.set(issue.id, issue.updatedAt)
      }
    }
    saveSeenStatuses(seen)
    saveSeenUpdatedAt(seenUpdatedAt)
  }, [issues, notifPermission, startTabBlink])

  // Notify when a run transitions from "running" → finished (succeeded / failed / cancelled)
  useEffect(() => {
    if (!agentRuns.length) return
    const seenRuns = seenRunStatusesRef.current
    const TERMINAL = new Set(['succeeded', 'failed', 'cancelled'])

    for (const run of agentRuns) {
      const prevStatus = seenRuns.get(run.id)
      const justFinished = prevStatus === 'running' && TERMINAL.has(run.status)
      if (justFinished) {
        const emoji = run.status === 'succeeded' ? '✅' : run.status === 'failed' ? '❌' : '⚪'
        const label = run.status === 'succeeded' ? 'abgeschlossen' : run.status === 'failed' ? 'fehlgeschlagen' : 'abgebrochen'
        const duration = run.finishedAt ? ` (${formatRunDuration(run.startedAt, run.finishedAt)})` : ''
        const title = `${emoji} ${run.agentName}: Run ${label}${duration}`
        const tag = `paperclip-run-${run.id}-${run.status}`
        const sentToOS = notifPermission === 'granted' && !externalSentTagsRef.current.has(tag)
        setLastNotif({ title, body: run.agentName, tag, type: 'run_finished', attemptedAt: new Date().toISOString(), sentToOS })
        if (document.visibilityState !== 'visible') startTabBlink()
        if (notifPermission === 'granted') {
          sendBrowserNotification(title, run.agentName, tag, false, externalSentTagsRef.current, bcRef.current)
        }
      }
    }

    for (const run of agentRuns) {
      seenRuns.set(run.id, run.status)
    }
    saveSeenRunStatuses(seenRuns)
  }, [agentRuns, notifPermission, startTabBlink])

  async function requestPermission() {
    if (!('Notification' in window)) return
    const perm = await Notification.requestPermission()
    setNotifPermission(perm)
  }

  const resetSeen = useCallback(() => {
    seenStatusesRef.current.clear()
    seenUpdatedAtRef.current.clear()
    saveSeenStatuses(seenStatusesRef.current)
    saveSeenUpdatedAt(seenUpdatedAtRef.current)
    setLastNotif(null)
    pollCountRef.current = 0
    void refetch()
  }, [refetch])

  function sendTestNotification() {
    if (!('Notification' in window)) return
    if (Notification.permission !== 'granted') {
      void requestPermission()
      return
    }
    // eslint-disable-next-line no-new
    new Notification('🔔 Paperclip Monitor: Test', {
      body: 'Browser-Benachrichtigungen funktionieren!',
      icon: '/favicon.ico',
      requireInteraction: false,
    })
  }

  function saveUserId() {
    const id = userIdInput.trim()
    if (!id) return
    setUserId(id)
    localStorage.setItem(STORAGE_USER_ID, id)
    seenStatusesRef.current.clear()
    saveSeenStatuses(seenStatusesRef.current)
    setShowSettings(false)
  }

  function saveProjectId() {
    const id = projectIdInput.trim()
    setProjectId(id)
    localStorage.setItem(STORAGE_PROJECT_ID, id)
  }

  function handleDefaultAgentChange(id: string) {
    setDefaultAgentId(id)
    localStorage.setItem(STORAGE_DEFAULT_AGENT_ID, id)
  }

  function toggleLabel(labelId: string) {
    setTrackedLabelIds(prev => {
      const next = prev.includes(labelId)
        ? prev.filter(id => id !== labelId)
        : [...prev, labelId]
      saveTrackedLabelIds(next)
      return next
    })
  }

  function removeLabel(labelId: string) {
    setTrackedLabelIds(prev => {
      const next = prev.filter(id => id !== labelId)
      saveTrackedLabelIds(next)
      return next
    })
  }

  function addLabelById() {
    const id = labelSearchInput.trim()
    if (!id || trackedLabelIds.includes(id)) return
    setTrackedLabelIds(prev => {
      const next = [...prev, id]
      saveTrackedLabelIds(next)
      return next
    })
    setLabelSearchInput('')
  }

  function getLabelObject(id: string): PaperclipLabel {
    return availableLabels.find(l => l.id === id) ?? { id, name: id }
  }

  const activeIssues = (issues ?? [])
    .filter(i => ACTIVE_DISPLAY_STATUSES.has(i.status))
    .sort((a, b) => {
      const aTime = a.updatedAt ? new Date(a.updatedAt).getTime() : 0
      const bTime = b.updatedAt ? new Date(b.updatedAt).getTime() : 0
      return bTime - aTime
    })
  const doneIssues = (issues ?? []).filter(i => i.status === 'done')
  const allMonitoredCount = activeIssues.length + doneIssues.length

  // Runs: live runs first, then sorted by finishedAt descending (most recently finished on top)
  const sortedRuns = [...agentRuns].sort((a, b) => {
    const aLive = a.status === 'running' ? 1 : 0
    const bLive = b.status === 'running' ? 1 : 0
    if (aLive !== bLive) return bLive - aLive
    const aTime = a.finishedAt ? new Date(a.finishedAt).getTime() : new Date(a.startedAt).getTime()
    const bTime = b.finishedAt ? new Date(b.finishedAt).getTime() : new Date(b.startedAt).getTime()
    return bTime - aTime
  })

  const filteredAvailableLabels = availableLabels.filter(l =>
    !labelSearchInput || l.name.toLowerCase().includes(labelSearchInput.toLowerCase()),
  )

  // Derive default agent display name for UI
  const defaultAgentName = availableAgents.find(a => a.id === defaultAgentId)?.name ?? defaultAgentId

  // Config validity for issue creation
  const canCreateIssue = projectId.trim().length > 0 && defaultAgentId.trim().length > 0

  return (
    <div className="flex h-full min-h-0 overflow-hidden">

      {/* ─── Left Sidebar: Todos & Offene Fragen ─────────────────────────── */}
      <div
        className={`flex flex-col h-full shrink-0 border-r bg-gray-50 transition-all duration-200 ${
          sidebarOpen ? 'w-[300px]' : 'w-10'
        }`}
      >
        {/* Toggle button */}
        <button
          type="button"
          onClick={toggleSidebar}
          className="flex items-center justify-between gap-1.5 px-2 py-3 text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-gray-100 transition-colors border-b"
          title={sidebarOpen ? 'Todos & Fragen einklappen' : 'Todos & Fragen ausklappen'}
        >
          {sidebarOpen ? (
            <>
              <span className="truncate">Todos & Fragen</span>
              <ChevronLeft className="h-3.5 w-3.5 shrink-0" />
            </>
          ) : (
            <ChevronRight className="h-3.5 w-3.5 mx-auto" />
          )}
        </button>

        {/* Panel content — only rendered when open */}
        {sidebarOpen && (
          <div className="flex-1 overflow-y-auto p-3">
            <MonitorEntriesPanel compact />
          </div>
        )}
      </div>

    <div className="flex flex-col gap-6 p-6 flex-1 overflow-y-auto min-w-0">
      <PageHeader
        title="Paperclip Monitor"
        description="Browser-Benachrichtigungen bei Issue-Statusänderungen"
        actions={
          <div className="flex items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (!canCreateIssue) {
                  setShowSettings(true)
                } else {
                  setShowCreateIssue(true)
                }
              }}
            >
              <Plus className="mr-1.5 h-4 w-4" />
              Neues Issue
            </Button>
            {notifPermission === 'granted' ? (
              <span className="flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
                <Bell className="h-3.5 w-3.5" />
                Benachrichtigungen aktiv
              </span>
            ) : notifPermission === 'denied' ? (
              <span className="flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-sm font-medium text-red-600">
                <BellOff className="h-3.5 w-3.5" />
                Im Browser blockiert
              </span>
            ) : (
              <Button variant="secondary" size="sm" onClick={requestPermission}>
                <Bell className="mr-1.5 h-4 w-4" />
                Benachrichtigungen erlauben
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowSettings(s => !s)}
              aria-label="Einstellungen"
            >
              <Settings className="h-4 w-4" />
            </Button>
          </div>
        }
      />

      {/* Diagnostics bar */}
      <div className="flex flex-col gap-2 rounded-lg border border-dashed px-4 py-3 text-xs text-muted-foreground">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span>
              <strong>{(issues ?? []).length}</strong> Issues überwacht
            </span>
            <span>
              Polls seit Seitenstart: <strong>{pollCountRef.current}</strong>
            </span>
            {dataUpdatedAt > 0 && (
              <span>
                Letzter Poll: <strong>{new Date(dataUpdatedAt).toLocaleTimeString('de-DE')}</strong>
              </span>
            )}
          </div>
          <Button variant="ghost" size="xs" onClick={resetSeen} title="Gesehene Status zurücksetzen — alle Issues gelten beim nächsten Poll als neu">
            <RefreshCw className="mr-1 h-3 w-3" />
            Status zurücksetzen
          </Button>
        </div>

        {lastNotif ? (
          <div
            className={`flex items-start gap-2 rounded border px-3 py-2 ${
              lastNotif.sentToOS ? 'border-green-300 bg-green-50' : 'border-yellow-300 bg-yellow-50'
            }`}
          >
            <span className="text-base leading-none shrink-0">{lastNotif.type === 'status_change' ? '🔔' : '📝'}</span>
            <div className="flex flex-col gap-0.5 min-w-0">
              <p className="font-semibold text-foreground truncate">{lastNotif.title}</p>
              <p className="truncate">{lastNotif.body}</p>
              <p>
                {new Date(lastNotif.attemptedAt).toLocaleTimeString('de-DE')}
                {' · '}
                {lastNotif.sentToOS ? (
                  <span className="text-green-700 font-medium">An Browser gesendet ✓</span>
                ) : notifPermission !== 'granted' ? (
                  <span className="text-red-600 font-medium">Nicht gesendet — Permission fehlt</span>
                ) : (
                  <span className="text-yellow-700 font-medium">Blockiert — anderer Tab hat bereits gesendet</span>
                )}
              </p>
            </div>
          </div>
        ) : (
          <p className="italic">
            Noch keine Benachrichtigung ausgelöst.
            {seenStatusesRef.current.size > 0
              ? ` ${seenStatusesRef.current.size} Issues als gesehen gespeichert — nur Änderungen lösen Benachrichtigungen aus.`
              : ' Beim nächsten Poll werden alle Issues als neu gewertet.'}
            {' '}Zum Testen: „Status zurücksetzen" drücken, dann wartet ein Poll (≤30 Sek.) und alle aktiven Issues lösen eine Benachrichtigung aus.
          </p>
        )}
      </div>

      {/* Settings panel */}
      {showSettings && (
        <Card>
          <Card.Body>
            <div className="flex flex-col gap-5 max-w-lg">
              {/* Issue-Erstellung: Projekt */}
              <div className="flex flex-col gap-2">
                <h3 className="text-sm font-semibold">Paperclip-Projekt für Issue-Erstellung</h3>
                <p className="text-xs text-muted-foreground">
                  Neu erstellte Issues werden diesem Projekt zugeordnet.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={projectIdInput}
                    onChange={e => setProjectIdInput(e.target.value)}
                    placeholder="Projekt-ID eingeben…"
                    onKeyDown={e => e.key === 'Enter' && saveProjectId()}
                  />
                  <Button variant="primary" size="sm" onClick={saveProjectId}>
                    Speichern
                  </Button>
                </div>
                {projectId && (
                  <p className="text-xs text-muted-foreground">
                    Aktuell: <code className="font-mono text-xs">{projectId}</code>
                  </p>
                )}
              </div>

              {/* Issue-Erstellung: Standard-Agent */}
              <div className="flex flex-col gap-2 border-t pt-4">
                <h3 className="text-sm font-semibold">Standard-Agent für Issue-Erstellung</h3>
                <p className="text-xs text-muted-foreground">
                  Neue Issues werden standardmäßig diesem Agenten zugewiesen.
                </p>
                {availableAgents.length > 0 ? (
                  <SelectRoot
                    value={defaultAgentId || '__none__'}
                    onValueChange={v => handleDefaultAgentChange(v === '__none__' ? '' : v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Agent auswählen…" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— kein Agent —</SelectItem>
                      {availableAgents.map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>
                          {agent.name}
                          {agent.role ? ` (${agent.role})` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </SelectRoot>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={defaultAgentId}
                      onChange={e => handleDefaultAgentChange(e.target.value)}
                      placeholder="Agent-ID eingeben…"
                    />
                  </div>
                )}
                {defaultAgentId && (
                  <p className="text-xs text-muted-foreground">
                    Aktuell:{' '}
                    <code className="font-mono text-xs">
                      {defaultAgentName || defaultAgentId}
                    </code>
                  </p>
                )}
              </div>

              {/* User ID */}
              <div className="flex flex-col gap-2 border-t pt-4">
                <h3 className="text-sm font-semibold">Paperclip Benutzer-ID</h3>
                <p className="text-xs text-muted-foreground">
                  Issues die du erstellt hast sowie deren Subtasks werden auf Statusänderungen überwacht.
                </p>
                <div className="flex gap-2">
                  <Input
                    value={userIdInput}
                    onChange={e => setUserIdInput(e.target.value)}
                    placeholder="Paperclip User-ID eingeben…"
                    onKeyDown={e => e.key === 'Enter' && saveUserId()}
                  />
                  <Button variant="primary" size="sm" onClick={saveUserId}>
                    Speichern
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Aktuell: <code className="font-mono text-xs">{userId}</code>
                </p>
              </div>

              {/* Label tracking */}
              <div className="flex flex-col gap-2 border-t pt-4">
                <h3 className="text-sm font-semibold">Labels tracken</h3>
                <p className="text-xs text-muted-foreground">
                  Alle Issues mit diesen Labels werden zusätzlich überwacht — unabhängig davon, wer sie erstellt hat.
                </p>

                {trackedLabelIds.length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {trackedLabelIds.map(id => (
                      <LabelChip
                        key={id}
                        label={getLabelObject(id)}
                        onRemove={() => removeLabel(id)}
                      />
                    ))}
                  </div>
                )}

                {availableLabels.length > 0 ? (
                  <div className="flex flex-col gap-2">
                    <Input
                      value={labelSearchInput}
                      onChange={e => setLabelSearchInput(e.target.value)}
                      placeholder="Label suchen…"
                    />
                    <div className="flex flex-wrap gap-1.5 max-h-32 overflow-y-auto rounded border p-2">
                      {filteredAvailableLabels.length === 0 && (
                        <span className="text-xs text-muted-foreground">Kein Label gefunden</span>
                      )}
                      {filteredAvailableLabels.map(label => {
                        const tracked = trackedLabelIds.includes(label.id)
                        return (
                          <button
                            key={label.id}
                            type="button"
                            onClick={() => toggleLabel(label.id)}
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium transition-opacity ${
                              tracked ? 'opacity-100 ring-2 ring-brand-400' : 'opacity-60 hover:opacity-100'
                            }`}
                            style={
                              label.color
                                ? {
                                    backgroundColor: `${label.color}22`,
                                    color: label.color,
                                    borderColor: `${label.color}44`,
                                  }
                                : undefined
                            }
                          >
                            <Tag className="h-3 w-3 shrink-0" />
                            {label.name}
                          </button>
                        )
                      })}
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Input
                      value={labelSearchInput}
                      onChange={e => setLabelSearchInput(e.target.value)}
                      placeholder="Label-ID eingeben…"
                      onKeyDown={e => e.key === 'Enter' && addLabelById()}
                    />
                    <Button variant="secondary" size="sm" onClick={addLabelById}>
                      Hinzufügen
                    </Button>
                  </div>
                )}
              </div>

              {/* Notification test */}
              <div className="flex flex-col gap-2 border-t pt-4">
                <h3 className="text-sm font-semibold">Benachrichtigungen testen</h3>
                <p className="text-xs text-muted-foreground">
                  Sendet sofort eine Test-Benachrichtigung um zu prüfen ob der Browser Notifications zulässt.
                </p>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={sendTestNotification}
                  className="self-start"
                >
                  <Bell className="mr-1.5 h-4 w-4" />
                  Benachrichtigung sofort senden
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Config warning when project/agent not set */}
      {!canCreateIssue && !showSettings && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
          <strong>Hinweis:</strong> Für die Issue-Erstellung müssen in den Einstellungen ein{' '}
          <button
            type="button"
            className="underline hover:no-underline"
            onClick={() => setShowSettings(true)}
          >
            Paperclip-Projekt und ein Standard-Agent
          </button>{' '}
          hinterlegt werden.
        </div>
      )}

      {/* Issue creation modal */}
      <CreateIssueModal
        open={showCreateIssue}
        onClose={() => {
          setShowCreateIssue(false)
          // Refresh monitor list after potential new issue
          void queryClient.invalidateQueries({ queryKey: ['paperclip-monitor-issues'] })
        }}
        defaultAgentId={defaultAgentId}
        projectId={projectId}
        agents={availableAgents}
        preselectedLabelIds={trackedLabelIds}
        availableLabels={availableLabels}
        onCreated={handleNewIssueCreated}
      />

      {/* Content */}
      {isLoading && <LoadingState label="Issues werden geladen…" />}

      {isError && (
        <ErrorState
          title="Issues konnten nicht geladen werden"
          details={error instanceof Error ? error.message : String(error)}
        />
      )}

      {!isLoading && !isError && allMonitoredCount === 0 && sortedRuns.length === 0 && (
        <EmptyState
          title="Keine aktiven Issues"
          description="Keine Issues befinden sich aktuell in Review, Blocked oder Done."
        />
      )}

      {!isLoading && !isError && allMonitoredCount > 0 && (
        <div className="flex flex-col gap-6">
          {activeIssues.length > 0 && (
            <section>
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
                <span>Aktiv</span>
                <span>({activeIssues.length})</span>
              </h2>
              <div className="flex flex-col gap-2">
                {activeIssues.map(issue => {
                  const isBlocked = issue.status === 'blocked'
                  return (
                    <a
                      key={issue.id}
                      href={getIssueUrl(issue.identifier)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group"
                      onClick={() => handleIssueClick(issue)}
                    >
                      <Card
                        className={`transition-colors hover:shadow-sm cursor-pointer ${
                          isBlocked
                            ? 'border-red-300 bg-red-50 hover:border-red-400'
                            : 'hover:border-brand-300'
                        }`}
                      >
                        <Card.Body>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex flex-col gap-1 min-w-0">
                              {issue.isSubtask && issue.parentIdentifier && (
                                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <span className="font-mono">{issue.parentIdentifier}</span>
                                  <ChevronRight className="h-3 w-3 shrink-0" />
                                  <span className="truncate">{issue.parentTitle}</span>
                                </p>
                              )}
                              <div className="flex items-baseline gap-2">
                                <span className="font-mono text-xs text-muted-foreground shrink-0">
                                  {issue.identifier}
                                </span>
                                <span
                                  className={`text-sm font-medium leading-snug ${
                                    isBlocked
                                      ? 'text-red-800 group-hover:text-red-900'
                                      : 'group-hover:text-brand-600'
                                  }`}
                                >
                                  {issue.title}
                                </span>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {issue.updatedAt && (
                                  <p className="text-xs text-muted-foreground">
                                    {new Date(issue.updatedAt).toLocaleString('de-DE')}
                                  </p>
                                )}
                                {issue.matchedLabels && issue.matchedLabels.length > 0 && (
                                  <div className="flex gap-1 flex-wrap">
                                    {issue.matchedLabels.map(l => (
                                      <LabelChip key={l.id} label={l} />
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <StatusBadge
                                status={issue.status}
                                label={STATUS_LABELS[issue.status] ?? issue.status}
                              />
                              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </a>
                  )
                })}
              </div>
            </section>
          )}

          {doneIssues.length > 0 && (
            <section>
              <button
                type="button"
                onClick={() => setShowDone(s => !s)}
                className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide hover:text-foreground transition-colors"
              >
                <StatusBadge status="done" label={STATUS_LABELS['done']} />
                <span>({doneIssues.length})</span>
                {showDone ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
              </button>
              {showDone && (
                <div className="flex flex-col gap-2">
                  {doneIssues.map(issue => (
                    <a
                      key={issue.id}
                      href={getIssueUrl(issue.identifier)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block group"
                      onClick={() => handleIssueClick(issue)}
                    >
                      <Card className="transition-colors hover:border-brand-300 hover:shadow-sm cursor-pointer opacity-75">
                        <Card.Body>
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex flex-col gap-1 min-w-0">
                              {issue.isSubtask && issue.parentIdentifier && (
                                <p className="flex items-center gap-1 text-xs text-muted-foreground">
                                  <span className="font-mono">{issue.parentIdentifier}</span>
                                  <ChevronRight className="h-3 w-3 shrink-0" />
                                  <span className="truncate">{issue.parentTitle}</span>
                                </p>
                              )}
                              <div className="flex items-baseline gap-2">
                                <span className="font-mono text-xs text-muted-foreground shrink-0">
                                  {issue.identifier}
                                </span>
                                <span className="text-sm font-medium leading-snug group-hover:text-brand-600">
                                  {issue.title}
                                </span>
                              </div>
                              {issue.updatedAt && (
                                <p className="text-xs text-muted-foreground">
                                  {new Date(issue.updatedAt).toLocaleString('de-DE')}
                                </p>
                              )}
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <StatusBadge status="done" label={STATUS_LABELS['done']} />
                              <ExternalLink className="h-3.5 w-3.5 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                            </div>
                          </div>
                        </Card.Body>
                      </Card>
                    </a>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {/* ─── Agent Runs ──────────────────────────────────────────────────── */}
      {sortedRuns.length > 0 && (
        <section>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-muted-foreground uppercase tracking-wide">
            <span>Agent Runs</span>
            <span>({sortedRuns.length})</span>
          </h2>
          <div className="flex flex-col gap-1.5">
            {sortedRuns.map(run => {
              const isRunning = run.status === 'running'
              const displayStatus = run.status === 'succeeded' ? 'done' : run.status
              const displayLabel = isRunning
                ? 'Live'
                : run.status === 'succeeded'
                  ? 'OK'
                  : run.status === 'failed'
                    ? 'Fehler'
                    : 'Abgebrochen'
              return (
                <Card
                  key={run.id}
                  className={`transition-colors ${isRunning ? 'border-brand-300 bg-brand-50' : 'opacity-80 hover:opacity-100'}`}
                >
                  <Card.Body>
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex flex-col gap-0.5 min-w-0">
                        <span className="text-sm font-medium truncate">{run.agentName}</span>
                        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                          {isRunning ? (
                            <span className="flex items-center gap-1 text-brand-600 font-medium">
                              <Loader2 className="h-3 w-3 animate-spin shrink-0" />
                              {formatRunDuration(run.startedAt, null)}
                            </span>
                          ) : (
                            <>
                              <span>{formatRunDuration(run.startedAt, run.finishedAt)}</span>
                              {run.finishedAt && (
                                <>
                                  <span>·</span>
                                  <span>{formatFinishedAgo(run.finishedAt)}</span>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                      <StatusBadge status={displayStatus} label={displayLabel} />
                    </div>
                  </Card.Body>
                </Card>
              )
            })}
          </div>
        </section>
      )}
    </div>
    <TrackingTimeline />
    </div>
  )
}
